import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { ArrowLeft, Compass, Sparkles, Route, Trophy, Swords } from "lucide-react";
import { toast } from "sonner";
import { fetchCountries, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { earnPoints, deviceId } from "@/lib/usePoints";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import { loadProfile } from "@/lib/tripProfile";

const pick = (o, lang) => (o && (o[lang] || o.en)) || "";
const fetchTrip = async (code) => (await axios.get(`${API}/trip/${code}`)).data;
const fetchBoard = async () => (await axios.get(`${API}/guess/leaderboard`, { params: { device_id: deviceId() } })).data;
const NAME_KEY = "atlas.guess.name";
const myName = () => storage.get(NAME_KEY) || loadProfile()?.name || "";

function Leaderboard({ onNameSaved }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["guess-board"], queryFn: fetchBoard });
  const [name, setName] = useState(myName);
  const save = async () => {
    const n = name.trim(); if (n.length < 2) return;
    storage.set(NAME_KEY, n); await onNameSaved(n); qc.invalidateQueries({ queryKey: ["guess-board"] }); toast.success(t("guess_name_saved"));
  };
  const rows = data?.top || [];
  return (
    <section className="rounded-3xl border border-stoneLine bg-card p-4 mt-6" data-testid="guess-leaderboard">
      <h2 className="font-display font-bold text-lg text-ink inline-flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> {t("guess_board_title")}</h2>
      <p className="text-xs text-muted-foreground mb-3">{t("guess_board_sub").replace("{n}", data?.total_players || 0)}</p>
      <div className="flex gap-2 mb-3">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={24} placeholder={t("guess_name_placeholder")} className="flex-1 rounded-full border border-stoneLine bg-sand/40 px-4 py-2 text-sm" data-testid="guess-name-input" />
        <button onClick={save} disabled={name.trim().length < 2} className="rounded-full bg-ink text-sand px-4 py-2 text-sm font-semibold disabled:opacity-40" data-testid="guess-name-save">{t("save")}</button>
      </div>
      <ol className="divide-y divide-stoneLine" data-testid="guess-board-rows">
        {rows.map((r) => (
          <li key={r.rank} className={`flex items-center gap-3 py-2 text-sm ${r.me ? "font-bold text-ochre" : "text-ink"}`} data-testid={`board-row-${r.rank}`}>
            <span className="w-6 text-center stat-number">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : r.rank}</span>
            <span className="flex-1 truncate">{r.name}{r.me ? ` (${t("guess_you")})` : ""}</span>
            <span className="text-xs text-muted-foreground">{r.wins}/{r.played}</span>
            <span className="stat-number w-10 text-end">{r.points}</span>
          </li>
        ))}
        {!rows.length && <li className="py-3 text-sm text-muted-foreground">{t("guess_board_empty")}</li>}
      </ol>
      {data?.me && data.me.rank > 20 && <p className="mt-2 text-sm text-ochre font-semibold" data-testid="board-me">#{data.me.rank} · {data.me.name} · {data.me.points}</p>}
    </section>
  );
}

/** "Guess the destination": friends see the stops of a shared itinerary and pick the country among 23 flags (3 tries). */
export default function Guess() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const code = (params.get("c") || "").toUpperCase();
  const by = (params.get("by") || "").slice(0, 24);
  const days = Number(params.get("d")) === 5 ? 5 : 3;
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const { data: trip, isError } = useQuery({ queryKey: ["trip", code], queryFn: () => fetchTrip(code), enabled: Boolean(code), retry: false });
  const doneKey = `atlas.guess.${code}`;
  const [tries, setTries] = useState([]);
  const [result, setResult] = useState(() => storage.get(doneKey) || null);
  const list = useMemo(() => [...countries].sort((a, b) => (a.short_names[lang] || "").localeCompare(b.short_names[lang] || "", lang)), [countries, lang]);
  const plan = trip ? (days === 3 ? trip.short : [0, 1, 2, 3, 4]).map((i) => trip.days[i]) : [];
  const revealed = Math.min(plan.length, tries.length + 1);
  const country = countries.find((c) => c.code === code);

  const post = (attempts, name = myName()) => axios.post(`${API}/guess/result`, { device_id: deviceId(), name, code, attempts, by }).then(() => qc.invalidateQueries({ queryKey: ["guess-board"] })).catch(() => {});
  useEffect(() => { if (by) trackEvent("trip_guess_open", { code, by: 1 }); }, [code, by]);

  const guess = (c) => {
    if (result || tries.includes(c)) return;
    trackEvent("trip_guess", { code, pick: c, attempt: tries.length + 1 });
    if (c === code) {
      const r = `won${tries.length + 1}`; setResult(r); storage.set(doneKey, r); toast.dismiss();
      earnPoints("trip_guess", lang); post(tries.length + 1); toast.success(t("guess_won").replace("{n}", tries.length + 1).replace("{p}", 3 - tries.length), { icon: "🎉" });
    } else {
      const next = [...tries, c]; setTries(next);
      if (next.length >= 3) { setResult("lost"); storage.set(doneKey, "lost"); post(0); } else toast(t("guess_wrong"), { icon: "🧭" });
    }
  };
  if (!code || isError) return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-muted-foreground" data-testid="guess-page">{t("guess_missing")}</div>;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 sm:py-10 pb-24 md:pb-10" data-testid="guess-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline mb-4" data-testid="guess-back"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}</Link>
      <div className="flex items-end gap-3 mb-4">
        <span className="w-12 h-12 rounded-2xl bg-[#5B4B8A] text-white flex items-center justify-center shrink-0"><Compass className="w-6 h-6" /></span>
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-ink leading-tight" data-testid="guess-title">{t("guess_title")}</h1>
          {by && <p className="text-sm font-semibold text-[#5B4B8A] inline-flex items-center gap-1" data-testid="guess-by"><Swords className="w-3.5 h-3.5" /> {t("guess_by").replace("{name}", by)}</p>}
          <p className="text-sm text-muted-foreground">{result ? t("guess_open_trip") : t("guess_sub").replace("{n}", 3 - tries.length)}</p>
        </div>
      </div>
      <ol className="space-y-2 mb-5" data-testid="guess-hints">
        {plan.slice(0, result ? plan.length : revealed).map((d, i) => (
          <li key={d.title.en} className="rounded-2xl border border-stoneLine bg-card p-3 text-sm" data-testid={`guess-hint-${i + 1}`}>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("day")} {i + 1}</span>
            <div className="text-ink">{d.stops.map((s) => pick(s.name, lang)).join(" · ")}</div>
          </li>
        ))}
        {!result && revealed < plan.length && <li className="text-xs text-muted-foreground">{t("guess_more_hints")}</li>}
      </ol>
      {result ? (
        <div className="rounded-3xl bg-ink text-sand p-5 flex flex-col items-start gap-3" data-testid="guess-result">
          <div className="inline-flex items-center gap-2 font-display font-bold text-2xl"><Sparkles className="w-5 h-5 text-ochre" /> {result === "lost" ? t("guess_lost") : t("guess_won").replace("{n}", result.slice(3)).replace("{p}", 4 - Number(result.slice(3)))}</div>
          <div className="flex items-center gap-2">{country && <img src={country.flag_url} alt="" className="w-10 h-7 rounded-sm object-cover" />}<span className="text-lg">{country?.short_names?.[lang]}</span></div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => navigate(`/itinerari?c=${code}&d=${days}`)} className="inline-flex items-center gap-2 rounded-full bg-ochre text-white px-5 py-2.5 font-semibold hover:bg-white hover:text-ink transition-colors" data-testid="guess-open-trip"><Route className="w-4 h-4" /> {t("guess_open_trip")}</button>
            <button onClick={() => navigate("/itinerari")} className="inline-flex items-center gap-2 rounded-full border border-sand/40 px-5 py-2.5 font-semibold hover:bg-white/10 transition-colors" data-testid="guess-counter"><Swords className="w-4 h-4" /> {t("guess_counter")}</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="guess-flags">
          {list.map((c) => <button key={c.code} onClick={() => guess(c.code)} disabled={tries.includes(c.code)} data-testid={`guess-flag-${c.code}`} className={`flex flex-col items-center gap-1 rounded-xl p-2 border transition-colors ${tries.includes(c.code) ? "opacity-30 border-transparent" : "border-stoneLine hover:border-ochre active:scale-95"}`}><img src={c.flag_url} alt={c.short_names[lang]} className="w-12 h-8 object-cover rounded-md border border-stoneLine" /><span className="text-[10px] font-semibold text-ink leading-tight text-center line-clamp-2 min-h-[24px]">{c.short_names[lang]}</span></button>)}
        </div>
      )}
      <Leaderboard onNameSaved={(n) => (result ? post(result === "lost" ? 0 : Number(result.slice(3)), n) : Promise.resolve())} />
    </div>
  );
}
